import { Box, Theme, Typography, useTheme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { ChevronDown } from "lucide-react";
import { ComponentProps, ReactNode } from "react";
import {NoteAttachmentView} from "../dtos";
import {NoteAttachmentList} from "./note-attachments";

/**
 * Shared Figma "Loan process" field primitives: every form input renders as a small
 * caption label ABOVE a 36px white box (no MUI floating labels) — see field-shell
 * consumers in form-fields.tsx / phone-field.tsx.
 */
export const FieldShell = ({
  label,
  help,
  error,
  returnedReason,
  returnedAttachments,
  children,
}: {
  label: string;
  help?: string;
  /** §Rules: the template validate-rule verdict for this field (design: red caption under the box) */
  error?: string;
  /**
   * §Field-review: the reviewer's reason when this field was sent back (fieldReviews[key].comment).
   * Distinct from `error`, which is the template's own live validate rule: this one is a person's
   * instruction about a value that already saved, so it OUTLIVES a keystroke and stays until the
   * reviewer clears it. Without it the CRM named the field in the "Needs fixing" banner and then
   * showed the agent no way to learn WHAT to fix — the reason was on the wire the whole time.
   */
  returnedReason?: string | null;
  returnedAttachments?: NoteAttachmentView[];
  children: ReactNode;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors.semantic;
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
        <Typography
          sx={{
            fontSize: 11,
            lineHeight: "16px",
            color: colors.text.secondary,
          }}
        >
          {label}
        </Typography>
        {returnedReason && (
          // workflow-manager marks a sent-back field with this chip beside the label; the CRM
          // carries the same word so the two screens read as one system to Ops and Sales.
          <Box
            sx={{
              px: 0.75,
              borderRadius: theme.nawy.radius.lg,
              border: `1px solid ${colors.text.extension.negative}`,
              color: colors.text.extension.negative,
              fontSize: 10,
              lineHeight: "16px",
              flexShrink: 0,
            }}
          >
            returned
          </Box>
        )}
      </Box>
      {children}
      {returnedReason && (
        <Typography
          sx={{
            fontSize: 11,
            lineHeight: "16px",
            color: colors.text.extension.negative,
            mt: 0.5,
            whiteSpace: "pre-line",
          }}
        >
          {`Returned: ${returnedReason}`}
        </Typography>
      )}
      <NoteAttachmentList attachments={returnedAttachments} />
      {error && (
        <Typography
          sx={{
            fontSize: 11,
            lineHeight: "16px",
            color: colors.text.extension.negative,
            mt: 0.5,
          }}
        >
          {error}
        </Typography>
      )}
      {help && (
        <Typography
          sx={{
            fontSize: 11,
            lineHeight: "16px",
            color: colors.text.tertiary,
            mt: 0.5,
            whiteSpace: "pre-line",
          }}
        >
          {help}
        </Typography>
      )}
    </Box>
  );
};

/** 36px white input box: border.primary at rest, accent on focus. Multiline swaps the fixed height for padding. */
export const fieldInputSx = (
  theme: Theme,
  opts: { multiline?: boolean } = {}
): SystemStyleObject<Theme> => {
  const colors = theme.nawy.colors.semantic;
  return {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.layer.primary,
      // §Radius (product ask, 2026-08-06): roomier corners on every text field
      borderRadius: theme.nawy.radius.xl,
      fontSize: 14,
      lineHeight: "20px",
      color: colors.text.primary,
      ...(opts.multiline ? { py: "8px", px: "12px" } : { height: 36 }),
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.primary,
      },
      "&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.primaryDark,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.accent,
        borderWidth: "1px",
      },
      "&.Mui-disabled": {
        backgroundColor: colors.layer.extension.disabledLight,
      },
      // §Rules: a failed template validate rule outlines the box red (design: Unit price mismatch)
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.extension.negative,
      },
    },
    "& .MuiOutlinedInput-input": opts.multiline
      ? { p: 0 }
      : { padding: "8px 12px", height: "unset" },
    // keep the value clear of the chevron indicator
    "& .MuiSelect-select.MuiOutlinedInput-input": { pr: "36px" },
    "& .MuiSelect-icon": {
      right: "12px",
      top: "calc(50% - 8px)",
      color: colors.text.secondary,
    },
    "& .MuiInputAdornment-root .MuiTypography-root": {
      fontSize: 14,
      color: colors.text.secondary,
    },
    // §MobileQA: the design's money suffix ("EGP") is a "Suffix selector" CHIP sitting inside the
    // box — Layer/Subtle ground, radius md, px 8 / py 2, 12/18 Text/Primary — not bare grey text.
    // Scoped to the END adornment so the grouped-row EGP *prefix* (finance-widgets) stays plain.
    "& .MuiInputAdornment-positionEnd": {
      backgroundColor: colors.layer.secondary,
      borderRadius: theme.nawy.radius.md,
      px: "8px",
      py: "2px",
    },
    "& .MuiInputAdornment-positionEnd .MuiTypography-root": {
      fontSize: 12,
      lineHeight: "18px",
      color: colors.text.primary,
    },
  };
};

/** Select indicator per the design (16px chevron); prop spread keeps MUI's icon classes/rotation. */
export const SelectChevron = (props: ComponentProps<typeof ChevronDown>) => (
  <ChevronDown size={16} {...props} />
);

/** Solid accent button (design: 32px, radius 8) — explicit colors: the theme defines no palette.primary. */
export const primaryButtonSx = (theme: Theme): SystemStyleObject<Theme> => ({
  whiteSpace: "nowrap",
  height: 32,
  px: 2,
  borderRadius: theme.nawy.radius.lg,
  // §ButtonType (product ask, 2026-08-10): 13/18 rather than the Figma's 14/20. A deliberate
  // divergence — the frames specify Body/md 14px, but at the CRM's density the action buttons
  // under a step card read heavier than the field labels above them.
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "18px",
  boxShadow: "none",
  backgroundColor: theme.nawy.colors.semantic.layer.accent,
  color: theme.nawy.colors.semantic.text.onColor,
  // §MobileQA: the design's disabled Button is a NEUTRAL Layer/Disabled #C7C7C7 ground with
  // Text/On Color ink — not the accent at half opacity, which still reads as "the primary CTA,
  // slightly faded". §BlueButtons governs the ENABLED look only.
  "&.Mui-disabled": {
    backgroundColor: theme.nawy.colors.semantic.layer.extension.disabled,
    color: theme.nawy.colors.semantic.text.onColor,
  },
  "&:hover": {
    boxShadow: "none",
    backgroundColor: theme.nawy.colors.primitives.blue[600],
  },
});

/** Destructive counterpart for terminal REJECTED actions (Disqualify, Reject) —
 *  outlined red so it can't be mistaken for the brand-filled Proceed next to it. */
export const destructiveButtonSx = (theme: Theme): SystemStyleObject<Theme> => ({
  whiteSpace: "nowrap",
  height: 32,
  px: 2,
  borderRadius: theme.nawy.radius.lg,
  // §ButtonType (product ask, 2026-08-10): 13/18 rather than the Figma's 14/20. A deliberate
  // divergence — the frames specify Body/md 14px, but at the CRM's density the action buttons
  // under a step card read heavier than the field labels above them.
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "18px",
  boxShadow: "none",
  border: `1px solid ${theme.nawy.colors.semantic.border.extension.negative}`,
  color: theme.nawy.colors.semantic.text.extension.negative,
  backgroundColor: theme.nawy.colors.semantic.layer.primary,
  "&.Mui-disabled": {
    border: `1px solid ${theme.nawy.colors.semantic.border.extension.negative}`,
    color: theme.nawy.colors.semantic.text.extension.negative,
    backgroundColor: theme.nawy.colors.semantic.layer.primary,
    opacity: 0.5,
  },
  "&:hover": {
    boxShadow: "none",
    backgroundColor: theme.nawy.colors.semantic.layer.extension.negativeLight,
    border: `1px solid ${theme.nawy.colors.semantic.border.extension.negative}`,
  },
});

/** Secondary actions (Cancel, Attach file, Share with client) — the TONAL blue button.
 *
 *  §BlueButtons (product ask, 2026-08-06) retired the white-ground/black-text neutral look and
 *  made every button brand blue. That was implemented by pointing this at the SAME accent fill and
 *  white ink as {@link primaryButtonSx} — byte-for-byte identical — so a secondary and a primary
 *  side by side became indistinguishable: "Cancel" looked exactly like "Save request", and
 *  "Upload on behalf" exactly like "Request from client". §MobileQA: the design draws secondaries
 *  tonally (Layer/Accent Light ground, Text/Accent ink) everywhere it pairs them with a primary —
 *  "Update details", "Add guarantor", "Share with client", "Escalate". That is still brand blue,
 *  so it honours §BlueButtons while restoring the hierarchy the pairing needs. */
export const secondaryButtonSx = (theme: Theme): SystemStyleObject<Theme> => ({
  whiteSpace: "nowrap",
  height: 32,
  px: 2,
  borderRadius: theme.nawy.radius.lg,
  // §ButtonType (product ask, 2026-08-10): 13/18 rather than the Figma's 14/20. A deliberate
  // divergence — the frames specify Body/md 14px, but at the CRM's density the action buttons
  // under a step card read heavier than the field labels above them.
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "18px",
  boxShadow: "none",
  border: "none",
  backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
  color: theme.nawy.colors.semantic.text.accent,
  // Same neutral disabled ground as {@link primaryButtonSx}: Figma draws ONE disabled state for the
  // Button component regardless of variant, and a faded tonal button barely reads as disabled at all.
  "&.Mui-disabled": {
    backgroundColor: theme.nawy.colors.semantic.layer.extension.disabled,
    color: theme.nawy.colors.semantic.text.onColor,
  },
  "&:hover": {
    boxShadow: "none",
    border: "none",
    backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
  },
});
